import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { jsPDF } from 'jspdf';

import { useProductDocuments } from '@/api/documents';
import { useCountries, useProductSearch } from '@/api/products';
import {
  uploadQuestionnaireFile,
  useCommitQuestionnaire,
  usePrepareQuestionnaire,
} from '@/api/questionnaires';
import type { CountrySummary, ProductSummary } from '@/api/types';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { paths } from '@/routes/paths';

import { type SubmitStep, useSubmitStepper } from './use-submit-stepper';

export type SubmitMode = 'file' | 'questions';

const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

interface SubmitWizardContextValue {
  mode: SubmitMode;
  step: SubmitStep;
  isFirst: boolean;
  isLast: boolean;
  canGoNext: boolean;
  goNext: () => void;
  goBack: () => void;
  submitting: boolean;
  submitError: string | null;
  handleSubmit: () => Promise<void>;
  productQuery: string;
  setProductQuery: (value: string) => void;
  products: Array<ProductSummary>;
  productsError: boolean;
  productsLoading: boolean;
  selectedProductName: string;
  selectProduct: (product: ProductSummary) => void;
  countryQuery: string;
  setCountryQuery: (value: string) => void;
  matchingCountries: Array<CountrySummary>;
  countriesError: boolean;
  countriesLoading: boolean;
  selectedCountryName: string;
  selectCountry: (country: CountrySummary) => void;
  questionnaireFile: File | null;
  setQuestionnaireFile: (file: File | null) => void;
  freeTextQuestions: string;
  setFreeTextQuestions: (value: string) => void;
  documents: ReturnType<typeof useProductDocuments>['data'];
  documentsLoading: boolean;
  selectedDocumentIds: Array<string>;
  toggleDocument: (id: string) => void;
  toggleDocuments: (ids: Array<string>) => void;
  extraDocuments: Array<File>;
  setExtraDocuments: (files: Array<File>) => void;
  productName: string;
  country: string;
}

const SubmitWizardContext = createContext<SubmitWizardContextValue | null>(null);

function renderFreeTextQuestionsToPdf(freeTextQuestions: string): File {
  const doc = new jsPDF();
  const lines = doc.splitTextToSize(freeTextQuestions, 180);
  doc.text(lines, 10, 10);
  const blob = doc.output('blob');
  return new File([blob], 'questions.pdf', { type: 'application/pdf' });
}

export function SubmitWizardProvider({
  mode,
  children,
}: {
  mode: SubmitMode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { step, goNext: goToNextStep, goBack, isFirst, isLast } = useSubmitStepper();

  const [productQuery, setProductQuery] = useState('');
  // The product's own id, used for the document lookup: the server expands it into every
  // code the Document Lake may have filed a document under. `productId` below stays the
  // displayed BP/PIM number, which is what the submission filename is built from.
  const [productKey, setProductKey] = useState('');
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [countryQuery, setCountryQuery] = useState('');
  const [country, setCountry] = useState('');
  const [countryName, setCountryName] = useState('');
  const [questionnaireFile, setQuestionnaireFile] = useState<File | null>(null);
  const [freeTextQuestions, setFreeTextQuestions] = useState('');
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Array<string>>([]);
  const [extraDocuments, setExtraDocuments] = useState<Array<File>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const debouncedProductQuery = useDebouncedValue(productQuery, PRODUCT_SEARCH_DEBOUNCE_MS);
  const { data: products, isError: productsError, isLoading: productsLoading } =
    useProductSearch(debouncedProductQuery);
  const { data: countries, isError: countriesError, isLoading: countriesLoading } =
    useCountries();
  const { data: documents, isLoading: documentsLoading } = useProductDocuments(
    step >= 2 ? productKey : '',
    country
  );
  const prepare = usePrepareQuestionnaire();
  const commit = useCommitQuestionnaire();

  const matchingCountries =
    countryQuery.trim().length > 0
      ? (countries ?? []).filter((candidate) =>
          candidate.name.toLowerCase().includes(countryQuery.trim().toLowerCase())
        )
      : (countries ?? []);

  const canGoNext = Boolean(
    productKey &&
      country &&
      (mode === 'file' ? questionnaireFile : freeTextQuestions.trim().length > 0)
  );

  const goNext = useCallback(() => {
    if (step === 1 && !canGoNext) {
      return;
    }
    if (step === 1 && mode === 'questions' && !questionnaireFile) {
      setQuestionnaireFile(renderFreeTextQuestionsToPdf(freeTextQuestions));
    }
    goToNextStep();
  }, [canGoNext, freeTextQuestions, goToNextStep, mode, questionnaireFile, step]);

  const selectProduct = useCallback((product: ProductSummary) => {
    setProductKey(product.id);
    setProductId(product.productId);
    setProductName(product.name);
    setProductQuery(product.name);
    setSelectedDocumentIds([]);
  }, []);

  const selectCountry = useCallback((selected: CountrySummary) => {
    setCountry(selected.code ?? selected.name);
    setCountryName(selected.name);
    setCountryQuery(selected.name);
    setSelectedDocumentIds([]);
  }, []);

  const toggleDocument = useCallback((id: string) => {
    setSelectedDocumentIds((previous) =>
      previous.includes(id)
        ? previous.filter((documentId) => documentId !== id)
        : [...previous, id]
    );
  }, []);

  const toggleDocuments = useCallback((ids: Array<string>) => {
    setSelectedDocumentIds((previous) => {
      const allSelected = ids.length > 0 && ids.every((id) => previous.includes(id));
      if (allSelected) {
        return previous.filter((id) => !ids.includes(id));
      }
      const next = new Set(previous);
      for (const id of ids) {
        next.add(id);
      }
      return [...next];
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!questionnaireFile) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const prepared = await prepare.mutateAsync({
        productId,
        productName,
        country,
        questionnaireFileName: questionnaireFile.name,
        extraDocumentFileNames: extraDocuments.map((file) => file.name),
      });

      await uploadQuestionnaireFile(
        prepared.timestamp,
        'questionnaire',
        questionnaireFile,
        questionnaireFile.type || 'application/octet-stream'
      );

      // "List" (the SharePoint list each ID was selected from, e.g. "Safety Data Sheet") is
      // required for the backend to resolve the ID unambiguously -- list-item IDs are only
      // unique within a single list, not across the whole site, so an ID alone is ambiguous.
      const documentsById = new Map((documents ?? []).map((document) => [document.id, document]));
      await uploadQuestionnaireFile(
        prepared.timestamp,
        'sharepoint-ids',
        new Blob([
          JSON.stringify({
            SharePoint_IDs: selectedDocumentIds.map((id) => ({
              ID: id,
              List: documentsById.get(id)?.drive ?? null,
            })),
          }),
        ]),
        'application/json'
      );

      for (const file of extraDocuments) {
        await uploadQuestionnaireFile(
          prepared.timestamp,
          'extra-document',
          file,
          file.type || 'application/pdf',
          file.name
        );
      }

      // Written last: the only call that actually triggers processing.
      await commit.mutateAsync(prepared.timestamp);
      await navigate({ to: paths.overview });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }, [
    commit,
    country,
    documents,
    extraDocuments,
    navigate,
    prepare,
    productId,
    productName,
    questionnaireFile,
    selectedDocumentIds,
  ]);

  const value = useMemo<SubmitWizardContextValue>(
    () => ({
      mode,
      step,
      isFirst,
      isLast,
      canGoNext: step === 1 ? canGoNext : true,
      goNext,
      goBack,
      submitting,
      submitError,
      handleSubmit,
      productQuery,
      setProductQuery,
      products: products ?? [],
      productsError,
      productsLoading,
      selectedProductName: productName,
      selectProduct,
      countryQuery,
      setCountryQuery,
      matchingCountries,
      countriesError,
      countriesLoading,
      selectedCountryName: countryName,
      selectCountry,
      questionnaireFile,
      setQuestionnaireFile,
      freeTextQuestions,
      setFreeTextQuestions,
      documents,
      documentsLoading,
      selectedDocumentIds,
      toggleDocument,
      toggleDocuments,
      extraDocuments,
      setExtraDocuments,
      productName,
      country,
    }),
    [
      canGoNext,
      countriesError,
      countriesLoading,
      country,
      countryName,
      countryQuery,
      documents,
      documentsLoading,
      extraDocuments,
      freeTextQuestions,
      goBack,
      goNext,
      handleSubmit,
      isFirst,
      isLast,
      matchingCountries,
      mode,
      productName,
      productQuery,
      products,
      productsError,
      productsLoading,
      questionnaireFile,
      selectCountry,
      selectProduct,
      selectedDocumentIds,
      step,
      submitError,
      submitting,
      toggleDocument,
      toggleDocuments,
    ]
  );

  return (
    <SubmitWizardContext.Provider value={value}>{children}</SubmitWizardContext.Provider>
  );
}

export function useSubmitWizard() {
  const context = useContext(SubmitWizardContext);
  if (!context) {
    throw new Error('useSubmitWizard must be used within SubmitWizardProvider');
  }
  return context;
}
